import React, { useState } from "react";
import PropTypes from "prop-types";
import { withFormik } from "formik";
import { useTranslation } from "react-i18next";
import moment from "moment";
import DatePicker from "../../../components/DatePicker/DatePickerInput";
import { getPaymentTypeOptions } from "../utils/payment-history.helpers";
import {
  FiltersContainer,
  FilterFormRow,
  FilterInputContainer,
  FilterDatePickerWrapper,
  LinkText,
  ButtonsContainer,
  FilterButton,
  FilterSelect,
  FieldsContainer
} from "../styles";

const SearchIcon = ({ disabled }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16.2812 13.8438L13.1562 10.7188C13 10.5938 12.8125 10.5 12.625 10.5H12.125C12.9688 9.40625 13.5 8.03125 13.5 6.5C13.5 2.9375 10.5625 0 7 0C3.40625 0 0.5 2.9375 0.5 6.5C0.5 10.0938 3.40625 13 7 13C8.5 13 9.875 12.5 11 11.625V12.1562C11 12.3438 11.0625 12.5312 11.2188 12.6875L14.3125 15.7812C14.625 16.0938 15.0938 16.0938 15.375 15.7812L16.25 14.9062C16.5625 14.625 16.5625 14.1562 16.2812 13.8438ZM7 10.5C4.78125 10.5 3 8.71875 3 6.5C3 4.3125 4.78125 2.5 7 2.5C9.1875 2.5 11 4.3125 11 6.5C11 8.71875 9.1875 10.5 7 10.5Z"
      fill={disabled ? "#0BBEF0" : "#0BBEF0"}
    />
  </svg>
);

const CalendarIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="2"
      y="3"
      width="20"
      height="19"
      rx="2"
      stroke="#475061"
      strokeWidth="1.5"
    />
    <path d="M2 9H22" stroke="#475061" strokeWidth="1.5" />
    <path d="M7 1V5" stroke="#475061" strokeWidth="1.5" strokeLinecap="round" />
    <path
      d="M17 1V5"
      stroke="#475061"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="7" cy="13" r="1" fill="#475061" />
    <circle cx="12" cy="13" r="1" fill="#475061" />
    <circle cx="17" cy="13" r="1" fill="#475061" />
    <circle cx="7" cy="17" r="1" fill="#475061" />
    <circle cx="12" cy="17" r="1" fill="#475061" />
    <circle cx="17" cy="17" r="1" fill="#475061" />
  </svg>
);

const Filters = ({
  values,
  setFieldValue,
  onFilter,
  campaigns,
  cleanFilters,
  disabled
}) => {
  const { t } = useTranslation();
  const [touchedIcon, setTouchedIcon] = useState(false);
  const paymentTypeOptions = getPaymentTypeOptions(t);

  return (
    <FiltersContainer>
      <FieldsContainer>
        <FilterFormRow background={true}>
          <FilterInputContainer widthSmartphone="100">
            <FilterSelect
              label={t("paymentsHistory.byCampaign")}
              name="campaignId"
              id="campaign-id"
              placeholder={t("paymentsHistory.byCampaign")}
              options={campaigns}
              onChange={(event) => {
                setFieldValue("campaignId", event.target.value);
              }}
              value={values.campaignId}
              disabled={disabled}
            />
          </FilterInputContainer>
        </FilterFormRow>
        <FilterFormRow background={true}>
          <FilterInputContainer widthSmartphone="100">
            <FilterDatePickerWrapper>
              <DatePicker
                data-testid="from_date"
                id="payments-date-picker-from"
                value={values.yearMonth}
                onChange={(dateSelected) => {
                  const yearMonth = `${moment(dateSelected).format(
                    "YYYY"
                  )}-${moment(dateSelected).format("M")}`;
                  setFieldValue("yearMonth", yearMonth);
                }}
                placeholder={t("paymentsHistory.byDate")}
                withPortal={true}
                format="MMMM yyyy"
                capitalizeDisplayValue={true}
                parseStringValueAsLocalDate={true}
                showMonthYearPicker={true}
                setTouchedIcon={setTouchedIcon}
                errorMessage={t(
                  "paymentsHistory.validations.date_by_month.required"
                )}
                disabled={disabled}
                isDisabled={disabled}
                customIcon={<CalendarIcon />}
              />
            </FilterDatePickerWrapper>
          </FilterInputContainer>
        </FilterFormRow>
        <FilterFormRow background={true}>
          <FilterInputContainer background={true} widthSmartphone="100">
            <FilterSelect
              label={t("paymentsHistory.byPaymentType")}
              name="paymentType"
              id="payment-type"
              placeholder={t("paymentsHistory.byPaymentType")}
              options={paymentTypeOptions}
              onChange={(event) => {
                setFieldValue("paymentType", event.target.value);
              }}
              value={values.paymentType}
              disabled={disabled}
            />
          </FilterInputContainer>
        </FilterFormRow>
      </FieldsContainer>
      <ButtonsContainer>
        <FilterButton
          id="filter-button"
          onClick={() => onFilter(values)}
          disabled={disabled}
        >
          {t("paymentsHistory.searchButton")}
          <SearchIcon disabled={disabled} />
        </FilterButton>
        <LinkText
          $disabled={disabled}
          aria-disabled={disabled}
          id="clean-filters"
          onClick={() => {
            if (disabled) {
              return;
            }
            setFieldValue("campaignId", "");
            setFieldValue("yearMonth", "");
            setFieldValue("paymentType", "");
            cleanFilters();
          }}
        >
          {t("paymentsHistory.cleanFilters")}
        </LinkText>
      </ButtonsContainer>
    </FiltersContainer>
  );
};

Filters.propTypes = {
  values: PropTypes.shape({
    campaignId: PropTypes.string,
    yearMonth: PropTypes.string,
    paymentType: PropTypes.string
  }).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  onFilter: PropTypes.func.isRequired,
  cleanFilters: PropTypes.func.isRequired,
  campaigns: PropTypes.array.isRequired,
  disabled: PropTypes.bool
};

export default withFormik({
  mapPropsToValues: () => ({
    campaignId: "",
    yearMonth: "",
    paymentType: ""
  })
})(Filters);
